<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import EmployeeDashboardCard from '@/components/dashboard/EmployeeDashboardCard.vue'
import EmployeeMetricCard from '@/components/dashboard/EmployeeMetricCard.vue'
import EmployeeQueueRow from '@/components/dashboard/EmployeeQueueRow.vue'
import EmployeeQuickAction from '@/components/dashboard/EmployeeQuickAction.vue'

type DashboardPayload = {
  user?: {
    name?: string
    company_name?: string
    crew_level?: string
    position?: string
    location?: string
    is_supervisor?: boolean
  }
  stats?: {
    ytd_gross?: number
    ytd_net?: number
    hours_this_week?: number
    open_tickets?: number
    reports_due?: number
    certs_expiring?: number
    next_pay_date?: string
    crew_on_site?: number
  }
  today?: {
    job_title?: string
    well_name?: string
    operator?: string
    customer?: string
    location?: string
    service_type?: string
    shift?: string
    start_time?: string
    status?: string
  } | null
  dispatch?: Array<Record<string, any>>
  tickets?: Array<Record<string, any>>
  tank_watch?: Array<Record<string, any>>
  sheets?: Array<Record<string, any>>
  certifications?: Array<Record<string, any>>
  schedule?: Array<Record<string, any>>
  messages?: Array<Record<string, any>>
  paystubs?: Array<Record<string, any>>
  activity?: Array<Record<string, any>>
}

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const now = ref(new Date())

const dashboard = ref<DashboardPayload>({
  user: {
    name: 'Jake Greenhat',
    company_name: 'Demo Oil Services Inc.',
    crew_level: 'Green Hat Hand',
    position: 'Flowback Hand',
    location: 'North Dakota',
    is_supervisor: false,
  },
  stats: {
    ytd_gross: 0,
    ytd_net: 0,
    hours_this_week: 0,
    open_tickets: 0,
    reports_due: 0,
    certs_expiring: 0,
    next_pay_date: '—',
    crew_on_site: 0,
  },
  today: null,
  dispatch: [],
  tickets: [],
  tank_watch: [],
  sheets: [],
  certifications: [],
  schedule: [],
  messages: [],
  paystubs: [],
  activity: [],
})

const userName = computed(() => dashboard.value.user?.name || 'Employee')
const isSupervisor = computed(() => !!dashboard.value.user?.is_supervisor)
const stats = computed(() => dashboard.value.stats || {})
const dispatch = computed(() => dashboard.value.dispatch || [])
const tickets = computed(() => dashboard.value.tickets || [])
const tankWatch = computed(() => dashboard.value.tank_watch || [])
const sheets = computed(() => dashboard.value.sheets || [])
const certifications = computed(() => dashboard.value.certifications || [])
const schedule = computed(() => dashboard.value.schedule || [])
const messages = computed(() => dashboard.value.messages || [])
const paystubs = computed(() => dashboard.value.paystubs || [])
const activity = computed(() => dashboard.value.activity || [])

const money = (value: unknown) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value || 0))

const todayLabel = computed(() =>
  now.value.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
)

function go(path: string) {
  router.push(path)
}

async function loadDashboard(showSpinner = true) {
  if (showSpinner) loading.value = true
  refreshing.value = !showSpinner
  error.value = ''

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/employee/dashboard', {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

    if (!response.ok) throw new Error('Unable to load employee dashboard')

    const payload = await response.json()

    dashboard.value = {
      ...dashboard.value,
      ...payload,
      user: {
        ...dashboard.value.user,
        ...(payload.user || payload.employee || {}),
        name: (payload.user || payload.employee)?.name
          || [payload.employee?.first_name, payload.employee?.last_name].filter(Boolean).join(' ')
          || dashboard.value.user?.name,
        company_name: (payload.user || payload.employee)?.company_name || payload.employee?.companyName || dashboard.value.user?.company_name,
        crew_level: (payload.user || payload.employee)?.crew_level || payload.employee?.crewLevelLabel || dashboard.value.user?.crew_level,
        is_supervisor: (payload.user || payload.employee)?.is_supervisor ?? payload.employee?.isSupervisor ?? false,
      },
      paystubs: payload.paystubs || payload.recent_paystubs || payload.payments || dashboard.value.paystubs,
      stats: {
        ...dashboard.value.stats,
        ...(payload.stats || payload.dashboard_stats || {}),
        ytd_gross: (payload.stats || {}).ytd_gross ?? (payload.stats || {}).ytdGross ?? 0,
        ytd_net: (payload.stats || {}).ytd_net ?? (payload.stats || {}).ytdNet ?? 0,
        reports_due: (payload.stats || {}).reports_due ?? (payload.stats || {}).pendingDailyReports ?? 0,
      },
    }
  } catch (e: any) {
    error.value = e?.message || 'Employee dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    now.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <AppLayout page-title="Employee Dashboard" page-subtitle="Field work, hours, and payroll">
    <div class="mx-auto w-full max-w-[1600px]">
        <!-- Context strip -->
        <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/45 p-4 shadow-xl shadow-black/10 backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
          <div class="flex min-w-0 items-center gap-3">
            <div class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 font-black text-slate-950 shadow-lg shadow-emerald-500/15">
              {{ userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() }}
            </div>

            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="truncate text-2xl font-extrabold tracking-tight text-white">
                  My Field Dashboard
                </h1>

                <span class="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-300">
                  {{ dashboard.user?.crew_level || 'W-2 Employee' }}
                </span>
              </div>

              <p class="mt-1 truncate text-sm text-slate-400">
                {{ userName }} · {{ dashboard.user?.company_name }} · {{ dashboard.user?.position }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2 text-sm">
            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Today</span>
              <strong class="ml-2 text-white">{{ todayLabel }}</strong>
            </div>

            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Location</span>
              <strong class="ml-2 text-white">{{ dashboard.user?.location || '—' }}</strong>
            </div>

            <button
              class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 font-semibold text-slate-200 transition hover:border-emerald-500/30 hover:text-emerald-300"
              :disabled="refreshing"
              @click="loadDashboard(false)"
            >
              {{ refreshing ? 'Refreshing…' : '↻ Refresh' }}
            </button>
          </div>
        </section>

        <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {{ error }}
        </div>

        <div v-if="loading" class="space-y-5">
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <div v-for="n in 5" :key="n" class="h-32 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40"></div>
          </div>
          <div class="grid gap-5 xl:grid-cols-12">
            <div class="h-80 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-7"></div>
            <div class="h-80 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-5"></div>
          </div>
        </div>

        <template v-else>
          <!-- Actionable KPIs -->
          <section class="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <EmployeeMetricCard
              title="Hours This Week"
              :value="stats.hours_this_week ?? 0"
              subtitle="Timesheet hours"
              icon="clock"
              tone="emerald"
              @click="go('/timesheets')"
            />
            <EmployeeMetricCard
              title="YTD Gross"
              :value="money(stats.ytd_gross)"
              subtitle="Payroll earnings"
              icon="money"
              tone="cyan"
              @click="go('/timesheets')"
            />
            <EmployeeMetricCard
              title="Open Tickets"
              :value="stats.open_tickets ?? 0"
              subtitle="Field tickets in progress"
              icon="ticket"
              tone="purple"
              @click="go('/employee/field-tickets')"
            />
            <EmployeeMetricCard
              title="Certs Expiring"
              :value="stats.certs_expiring ?? 0"
              subtitle="Renew before assignment"
              icon="warning"
              tone="amber"
              @click="go('/safety')"
            />
            <EmployeeMetricCard
              v-if="isSupervisor"
              title="Reports Due"
              :value="stats.reports_due ?? 0"
              subtitle="Daily reports to send"
              icon="report"
              tone="red"
              @click="go('/employee/daily-report')"
            />
            <EmployeeMetricCard
              v-else
              title="Crew On Site"
              :value="stats.crew_on_site ?? 0"
              subtitle="Current location headcount"
              icon="crew"
              tone="blue"
              @click="go('/employee/onsite')"
            />
          </section>

          <!-- Today + quick actions + messages -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <EmployeeDashboardCard
              class="xl:col-span-5"
              title="Today / Now"
              subtitle="Your active assignment"
              action-label="My Dispatch"
              @action="go('/employee/dispatch')"
            >
              <div v-if="dashboard.today" class="space-y-3">
                <div class="rounded-xl border border-emerald-500/20 bg-emerald-500/[.07] p-4">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <span class="text-[10px] font-bold uppercase tracking-[.14em] text-emerald-300">On Location</span>
                      <h3 class="mt-1 text-lg font-extrabold text-white">
                        {{ dashboard.today.well_name || dashboard.today.job_title }}
                      </h3>
                      <p class="mt-1 text-sm text-slate-400">
                        {{ dashboard.today.operator || dashboard.today.customer }} · {{ dashboard.today.service_type }}
                      </p>
                    </div>

                    <span class="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-300">
                      {{ dashboard.today.status || 'Active' }}
                    </span>
                  </div>

                  <div class="mt-4 grid gap-3 sm:grid-cols-3">
                    <div class="rounded-lg bg-slate-900/40 p-3">
                      <span class="block text-[10px] uppercase tracking-wide text-slate-500">Location</span>
                      <strong class="mt-1 block text-sm text-slate-100">{{ dashboard.today.location || '—' }}</strong>
                    </div>
                    <div class="rounded-lg bg-slate-900/40 p-3">
                      <span class="block text-[10px] uppercase tracking-wide text-slate-500">Shift</span>
                      <strong class="mt-1 block text-sm text-slate-100">{{ dashboard.today.shift || '—' }}</strong>
                    </div>
                    <div class="rounded-lg bg-slate-900/40 p-3">
                      <span class="block text-[10px] uppercase tracking-wide text-slate-500">Start</span>
                      <strong class="mt-1 block text-sm text-slate-100">{{ dashboard.today.start_time || '—' }}</strong>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-3 gap-2">
                  <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold text-slate-200 hover:border-emerald-500/30" @click="go('/timesheets')">Clock / Hours</button>
                  <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold text-slate-200 hover:border-emerald-500/30" @click="go('/employee/field-tickets')">Field Ticket</button>
                  <button class="rounded-xl border border-slate-700/50 bg-slate-700/30 px-3 py-3 text-xs font-semibold text-slate-200 hover:border-emerald-500/30" @click="go('/employee/onsite')">On Site Hub</button>
                </div>
              </div>

              <div v-else class="grid min-h-56 place-items-center rounded-xl border border-dashed border-slate-700/60 bg-slate-900/25 p-6 text-center">
                <div>
                  <div class="mx-auto grid h-12 w-12 place-items-center rounded-full bg-slate-700/40 text-xl">📍</div>
                  <p class="mt-3 font-semibold text-white">No active assignment</p>
                  <p class="mt-1 text-sm text-slate-500">Your next dispatch or scheduled rotation will appear here.</p>
                  <button class="mt-4 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950" @click="go('/employee/dispatch')">View Dispatch</button>
                </div>
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-4"
              title="Quick Actions"
              subtitle="Field-first shortcuts"
            >
              <div class="grid grid-cols-2 gap-3">
                <EmployeeQuickAction label="Timesheet" icon="time" tone="emerald" @click="go('/timesheets')" />
                <EmployeeQuickAction label="Field Ticket" icon="ticket" tone="purple" @click="go('/employee/field-tickets')" />
                <EmployeeQuickAction label="Tank Sheets" icon="tank" tone="cyan" @click="go('/employee/tank-sheets')" />
                <EmployeeQuickAction label="Well Head" icon="well" tone="blue" @click="go('/employee/well-head-sheets')" />
                <EmployeeQuickAction v-if="isSupervisor" label="Tanks" icon="tank" tone="amber" @click="go('/employee/tanks')" />
                <EmployeeQuickAction label="Tank Watch" icon="watch" tone="red" @click="go('/employee/tank-watch')" />
                <EmployeeQuickAction label="Safety" icon="shield" tone="emerald" @click="go('/safety')" />
                <EmployeeQuickAction v-if="isSupervisor" label="Daily Report" icon="report" tone="cyan" @click="go('/employee/daily-report')" />
                <EmployeeQuickAction v-else label="Schedule" icon="calendar" tone="cyan" @click="go('/scheduling')" />
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-3"
              title="Messages / Alerts"
              subtitle="Supervisor and system updates"
            >
              <div class="space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in messages.slice(0, 5)"
                  :key="item.id || i"
                  :title="item.title || item.sender || 'Message'"
                  :subtitle="item.message || item.description || ''"
                  :meta="item.time || item.created_at || ''"
                  :tone="item.priority === 'high' ? 'red' : item.priority === 'warning' ? 'amber' : 'cyan'"
                />
                <div v-if="!messages.length" class="grid min-h-52 place-items-center rounded-xl bg-slate-900/25 p-5 text-center">
                  <div>
                    <div class="text-2xl">✓</div>
                    <p class="mt-2 font-semibold text-white">No new messages</p>
                    <p class="mt-1 text-xs text-slate-500">Supervisor notes and alerts will appear here.</p>
                  </div>
                </div>
              </div>
            </EmployeeDashboardCard>
          </section>

          <!-- Work queue / field picture -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <EmployeeDashboardCard
              class="xl:col-span-4"
              title="Work Queue"
              subtitle="Items you need to finish"
            >
              <div class="space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in tickets.slice(0, 3)"
                  :key="`ticket-${item.id || i}`"
                  :title="item.ticket_number || item.title || 'Field Ticket'"
                  :subtitle="item.well_name || item.customer || item.service_type || ''"
                  :meta="item.status || ''"
                  :tone="item.status === 'approved' ? 'emerald' : item.status === 'rejected' ? 'red' : 'purple'"
                />

                <EmployeeQueueRow
                  v-for="(item, i) in sheets.slice(0, 3)"
                  :key="`sheet-${item.id || i}`"
                  :title="item.title || item.type || 'Field Sheet'"
                  :subtitle="item.well_name || item.location || ''"
                  :meta="item.status || item.time || ''"
                  :tone="item.status === 'overdue' ? 'red' : 'cyan'"
                />

                <div v-if="!tickets.length && !sheets.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  Nothing waiting on you right now.
                </div>
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-5"
              title="Tank Watch"
              subtitle="Location tank conditions"
              action-label="Open Tank Watch"
              @action="go('/employee/tank-watch')"
            >
              <div class="grid gap-3 sm:grid-cols-2">
                <div
                  v-for="(tank, i) in tankWatch.slice(0, 4)"
                  :key="tank.id || i"
                  class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <p class="font-bold text-white">{{ tank.name || `Tank ${i + 1}` }}</p>
                      <p class="mt-1 text-xs text-slate-500">{{ tank.location || dashboard.today?.location || 'Current location' }}</p>
                    </div>
                    <span
                      class="rounded-full px-2 py-1 text-[10px] font-bold"
                      :class="Number(tank.fill_pct || 0) >= 85 ? 'bg-red-500/15 text-red-300' : Number(tank.fill_pct || 0) >= 70 ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'"
                    >
                      {{ tank.fill_pct ?? 0 }}%
                    </span>
                  </div>

                  <div class="mt-4 h-2 overflow-hidden rounded-full bg-slate-700/50">
                    <div
                      class="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400"
                      :style="{ width: `${Math.min(100, Number(tank.fill_pct || 0))}%` }"
                    ></div>
                  </div>

                  <div class="mt-3 flex justify-between text-xs">
                    <span class="text-slate-500">Oil {{ tank.oil_bbl ?? 0 }} BBL</span>
                    <span class="text-slate-500">Water {{ tank.water_bbl ?? 0 }} BBL</span>
                  </div>
                </div>

                <div v-if="!tankWatch.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-6 text-center text-sm text-slate-500">
                  Tank conditions for your assigned location will appear here.
                </div>
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-3"
              title="Upcoming Schedule"
              subtitle="Next shifts and rotations"
              action-label="Open Schedule"
              @action="go('/scheduling')"
            >
              <div class="space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in schedule.slice(0, 5)"
                  :key="item.id || i"
                  :title="item.title || item.shift || 'Scheduled Shift'"
                  :subtitle="item.location || item.well_name || ''"
                  :meta="item.date || item.start_time || ''"
                  tone="emerald"
                />
                <div v-if="!schedule.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  No upcoming shifts loaded.
                </div>
              </div>
            </EmployeeDashboardCard>
          </section>

          <!-- Pay + certifications + activity -->
          <section class="grid gap-5 xl:grid-cols-12">
            <EmployeeDashboardCard
              class="xl:col-span-4"
              title="Pay Snapshot"
              subtitle="View-only payroll information"
              action-label="Open Payroll"
              @action="go('/timesheets')"
            >
              <div class="grid grid-cols-2 gap-3">
                <div class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <span class="text-xs text-slate-500">YTD Gross</span>
                  <strong class="mt-1 block text-xl text-white">{{ money(stats.ytd_gross) }}</strong>
                </div>
                <div class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <span class="text-xs text-slate-500">YTD Net</span>
                  <strong class="mt-1 block text-xl text-white">{{ money(stats.ytd_net) }}</strong>
                </div>
                <div class="col-span-2 rounded-xl border border-cyan-500/15 bg-cyan-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Next Pay Date</span>
                  <strong class="mt-1 block text-lg text-cyan-300">{{ stats.next_pay_date || '—' }}</strong>
                </div>
              </div>

              <div class="mt-4 space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in paystubs.slice(0, 3)"
                  :key="item.id || i"
                  :title="item.reference || item.date || `Paystub ${i + 1}`"
                  :subtitle="item.date || ''"
                  :meta="money(item.net || item.net_pay)"
                  tone="emerald"
                />
                <div v-if="!paystubs.length" class="rounded-xl border border-dashed border-slate-700/60 p-4 text-center text-sm text-slate-500">
                  Recent paystubs will appear here.
                </div>
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-4"
              title="Certifications"
              subtitle="Training and expiration status"
              action-label="Open Safety"
              @action="go('/safety')"
            >
              <div class="space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in certifications.slice(0, 5)"
                  :key="item.id || i"
                  :title="item.name || item.certification || 'Certification'"
                  :subtitle="item.expires_at ? `Expires ${item.expires_at}` : item.status || ''"
                  :meta="item.days_remaining != null ? `${item.days_remaining} days` : ''"
                  :tone="item.days_remaining != null && item.days_remaining <= 30 ? 'amber' : 'emerald'"
                />
                <div v-if="!certifications.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  Certification dates will appear here.
                </div>
              </div>
            </EmployeeDashboardCard>

            <EmployeeDashboardCard
              class="xl:col-span-4"
              title="Recent Activity"
              subtitle="Latest work updates"
            >
              <div class="space-y-2">
                <EmployeeQueueRow
                  v-for="(item, i) in activity.slice(0, 6)"
                  :key="item.id || i"
                  :title="item.title || item.description || 'Activity'"
                  :subtitle="item.description && item.title ? item.description : item.location || ''"
                  :meta="item.time || item.created_at || ''"
                  :tone="item.status === 'success' ? 'emerald' : item.status === 'warning' ? 'amber' : 'cyan'"
                />
                <div v-if="!activity.length" class="grid min-h-56 place-items-center rounded-xl border border-dashed border-slate-700/60 p-6 text-center">
                  <div>
                    <div class="text-2xl">▣</div>
                    <p class="mt-2 font-semibold text-white">No recent activity</p>
                    <p class="mt-1 text-sm text-slate-500">Completed dispatches, sheets and tickets will show here.</p>
                  </div>
                </div>
              </div>
            </EmployeeDashboardCard>
          </section>
        </template>
    </div>
  </AppLayout>
</template>
